import { useEffect, useState } from 'react';
import './progress-toast.css';

/**
 * Props para ProgressToast
 */
interface ProgressToastProps {
  /**
   * Mensaje principal a mostrar
   */
  message: string;

  /**
   * Mensaje de estado secundario (opcional)
   * @example "17,948 edges • 12,868 nodos"
   */
  statusMessage?: string;

  /**
   * Progreso de 0 a 100 (null = indeterminado)
   */
  progress?: number | null;

  /**
   * Tiempo estimado en segundos para completar (usado para progreso simulado)
   * Si se proporciona y progress es null, la barra se animará automáticamente
   */
  estimatedSeconds?: number;

  /**
   * Si el proceso está activo (visible)
   */
  visible: boolean;

  /**
   * Callback opcional para cancelar
   */
  onCancel?: () => void;
}

/**
 * ProgressToast - Notificación de progreso que emerge desde arriba
 *
 * @version 1.0
 * @updated 2026-03-18
 * @module core/components
 *
 * @description
 * Toast de progreso para operaciones largas que emerge desde el centro superior.
 * Soporta barra de progreso determinada (%) o indeterminada (animación continua).
 *
 * @remarks
 * **Características**:
 * - Posicionamiento fijo centrado (igual que SuccessToast)
 * - Animación slideDown al aparecer
 * - Barra de progreso con gradiente cyan (colores del sistema)
 * - Modo indeterminado con animación de shimmer
 * - Modo estimado: progreso simulado basado en tiempo
 * - Glassmorphism en estilo cyan (proceso activo)
 * - Sin emojis (cumple manifiesto)
 *
 * @example
 * ```tsx
 * // Progreso determinado
 * <ProgressToast
 *   message="Calculando sintaxis espacial..."
 *   statusMessage="17,948 edges • 12,868 nodos"
 *   progress={45}
 *   visible={true}
 * />
 *
 * // Progreso estimado (simulado)
 * <ProgressToast
 *   message="Calculando sintaxis espacial..."
 *   estimatedSeconds={30}
 *   visible={true}
 * />
 *
 * // Indeterminado
 * <ProgressToast
 *   message="Procesando red urbana..."
 *   visible={true}
 * />
 * ```
 */
export function ProgressToast({
  message,
  statusMessage,
  progress,
  estimatedSeconds,
  visible,
  onCancel,
}: ProgressToastProps): JSX.Element | null {
  // Estado para progreso simulado
  const [simulatedProgress, setSimulatedProgress] = useState(0);

  // Simular progreso basado en tiempo estimado
  useEffect(() => {
    if (!visible || progress !== undefined || !estimatedSeconds) {
      setSimulatedProgress(0);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      const ratio = elapsed / estimatedSeconds;

      // Curva logarítmica que avanza rápido al inicio y se frena gradualmente
      // Formula: 99 * (1 - e^(-3 * ratio)) - nunca llega exactamente a 99%
      // Esto da: 0s=0%, 33%tiempo=63%, 66%tiempo=86%, 100%tiempo=95%, 150%tiempo=98%
      const newProgress = 99 * (1 - Math.exp(-3 * ratio));
      setSimulatedProgress(Math.min(99, newProgress));
    }, 200);

    return () => clearInterval(interval);
  }, [visible, progress, estimatedSeconds]);

  if (!visible) return null;

  // Determinar valor de progreso a mostrar
  const displayProgress = progress ?? simulatedProgress;
  const isIndeterminate = progress === undefined && !estimatedSeconds;

  return (
    <div className="progress-toast" role="status" aria-live="polite">
      <div className="progress-toast-content">
        <div className="progress-toast-header">
          <span className="progress-toast-message">{message}</span>
          {onCancel && (
            <button
              className="progress-toast-cancel"
              onClick={onCancel}
              aria-label="Cancelar operación"
              type="button"
            >
              ✕
            </button>
          )}
        </div>

        <div
          className={`progress-toast-bar ${isIndeterminate ? 'indeterminate' : ''}`}
          role="progressbar"
          aria-valuenow={isIndeterminate ? undefined : Math.round(displayProgress)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="progress-toast-fill"
            style={isIndeterminate ? undefined : { width: `${displayProgress}%` }}
          />
        </div>

        <div className="progress-toast-footer">
          {statusMessage && <span className="progress-toast-status">{statusMessage}</span>}
          {!isIndeterminate && <span className="progress-toast-percent">{Math.round(displayProgress)}%</span>}
        </div>
      </div>
    </div>
  );
}
